/**
 * LOLER defect category (A/B/C) presentation — the single source of truth for
 * how a category is coloured and labelled, so a category reads the same on a
 * chip, in a donut slice and in a legend. (CX-416)
 *
 * COLOUR SCHEME: red / amber / blue, not red / amber / grey.
 *
 * The mockup's donut used blue for category C; grey was the other candidate.
 * Blue wins on measurable grounds rather than taste — running the three-slot
 * palette through a contrast/CVD validator, grey fails two checks that blue
 * passes:
 *
 *   - Chroma floor: a neutral grey has ~0 chroma, so it stops doing identity
 *     work and reads as "no data" rather than as a category. That reading is
 *     actively wrong here, because grey already means exactly that elsewhere in
 *     the app (PortfolioPieChart's empty-state slice) — and it is what we use
 *     below for genuinely uncategorised defects.
 *   - Contrast vs surface: mid-grey lands at 2.6:1 against the panel, under the
 *     3:1 floor for a chart mark.
 *
 * The exact steps are re-stepped from MUI's semantic tokens rather than taken
 * raw. `error.main`/`warning.main` (#D32F2F/#ED6C02) are adjacent hues and sit
 * at ΔE 13.2 under normal vision — below the 15 floor, i.e. hard to tell apart
 * even with full colour vision. Darkening the amber to #B88C00 lifts the worst
 * adjacent pair to ΔE 21.2 normal / 12.4 deuteranopia, and the full triple then
 * passes every check (lightness band, chroma floor, CVD separation,
 * normal-vision floor, contrast) against BOTH a light and a dark surface — so
 * the scheme survives a future dark theme unchanged.
 *
 * Colour is never the only channel: every consumer renders the category letter
 * and its meaning as text beside the swatch, because red↔amber remains the
 * weakest pair for deuteranopic readers even after re-stepping.
 */

export const DEFECT_SEVERITY_ORDER = ['A', 'B', 'C'] as const;

export type DefectSeverity = (typeof DEFECT_SEVERITY_ORDER)[number];

/**
 * Category colours for CHART MARKS (donut slices, swatches, chip borders).
 * Validated at the 3:1 contrast floor that applies to graphical objects.
 */
export const DEFECT_SEVERITY_COLORS: Record<DefectSeverity, string> = {
  A: '#C62828',
  B: '#B88C00',
  C: '#2D6FBE',
};

/**
 * Category colours for TEXT (the chip label). A separate set on purpose: the
 * mark colours above clear the 3:1 floor for graphical objects, but text has to
 * clear WCAG's 4.5:1, and category B's amber measures 3.09:1 on white — legible
 * as a donut slice, not as 0.7rem type. Same hues, darkened until they pass, so
 * a category still reads as itself in both roles.
 *
 * A and C already clear 4.5:1 (5.62:1 and 5.09:1) and are unchanged; only B
 * needed a darker step. Contrast on white: A 5.62:1, B 5.12:1, C 5.09:1.
 * Re-measured against #FFFFFF for AMP-741; the figures are unchanged.
 *
 * Removed as unused when the drawer's `ViolationItem` was deleted, which is a
 * regression this palette exists to prevent: nothing in a typecheck or a lint
 * run can see a chip label drop from 5.12:1 to 3.09:1. Restored with the chip.
 * `ElevatorViolationItem.test.tsx` pins the rendered colour so the next cleanup
 * has to break a test rather than a contrast floor.
 */
export const DEFECT_SEVERITY_TEXT_COLORS: Record<DefectSeverity, string> = {
  A: '#C62828',
  B: '#8A6900',
  C: '#2D6FBE',
};

/**
 * What each category means, in the words TLC uses on the LOLER report. Rendered
 * next to the colour everywhere, so category is never encoded by hue alone.
 */
export const DEFECT_SEVERITY_MEANINGS: Record<DefectSeverity, string> = {
  A: 'Immediate risk',
  B: 'Requires action',
  C: 'Observation',
};

/**
 * Open defects carrying no category at all (US/non-LOLER defects leave severity
 * null, as does a hand-created one). Grey is right *here* — this slice really is
 * "no category recorded" — which is the same reason it was wrong for category C.
 */
export const DEFECT_UNCATEGORIZED_COLOR = '#8A8A8A';
export const DEFECT_UNCATEGORIZED_LABEL = 'Uncategorised';

export function defectSeverityLabel(severity: DefectSeverity): string {
  return `Category ${severity}`;
}
